import React from 'react';
import PropTypes from 'prop-types';
import { Link, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Seo from '@laradevitt/gatsby-theme-just-basics/src/components/seo';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import { LogoMonogramWhite } from '../components/Brand';
import {
  Stack,
  Switcher
} from '../components/EveryLayout';
import { Section } from '../components/Layout';
import { FadeInOpacity } from '../components/Misc/FadeIn';
import {
  CaseStudyNav,
  ShareLinks,
} from '../components/Page';
import { RichTextField } from '../components/Page/PrismicFields';
import {
  Blockquote,
  InfoBoxes
} from '../components/Page/Slices';
import { ViewRelatedNews } from '../components/Page/Views';
import { collectHeadings, slugify } from '../helpers/headings';

/* eslint-disable react/prop-types */
export const Head = ({ data }) => {

  const { prismicCaseStudy } = data;

  let seoImageUrl = prismicCaseStudy.data.featured_image?.thumbnails?.OpenGraph?.url || prismicCaseStudy.data.featured_image?.url || '';
  if (prismicCaseStudy.data.main_image?.url) {
    seoImageUrl = prismicCaseStudy.data.main_image?.thumbnails?.OpenGraph?.url || prismicCaseStudy.data.main_image?.url;
  }

  return (
    <Seo
      title={prismicCaseStudy.data.title.text}
      description={prismicCaseStudy.data.teaser?.text || ''}
      image={seoImageUrl}
      path={`/impact/meet-our-clients/${prismicCaseStudy.uid}/`}
    />
  );
};
/* eslint-enable react/prop-types */

const CaseStudy = ({
  data,
}) => {
  const { site, prismicCaseStudy } = data;

  let mainImage = null;
  if (prismicCaseStudy.data.main_image?.gatsbyImageData) {
    mainImage = prismicCaseStudy.data.main_image;
    if (prismicCaseStudy.data.main_image.thumbnails?.Portrait?.gatsbyImageData) {
      mainImage = prismicCaseStudy.data.main_image.thumbnails.Portrait;
    }
  }
  else {
    if (prismicCaseStudy.data.featured_image?.url) {
      mainImage = prismicCaseStudy.data.featured_image;
    }
  }

  // Placeholder fact strip. Wire these to Prismic fields later — change only
  // this array (see docs/superpowers/specs/2026-06-28-case-study-layout-design.md).
  const factStrip = [
    { label: 'Client', value: 'Premier Credit' },
    { label: 'Country', value: 'Kenya' },
    { label: 'Sector', value: 'Microfinance' },
    { label: 'Year', value: '2025' },
  ];

  // Hero image orientation option: 'portrait' (default) or 'landscape'.
  // Wire to a Prismic field later if the client wants per-study control.
  const heroImageOrientation = 'portrait';
  const landscapeImage =
    prismicCaseStudy.data.main_image?.thumbnails?.Landscape?.gatsbyImageData
      ? prismicCaseStudy.data.main_image.thumbnails.Landscape
      : null;

  const headings = collectHeadings(prismicCaseStudy.data.body1);

  const headingComponents = {
    heading2: ({ node, children, key }) => (
      <h2 key={key} id={slugify(node.text)} className="scroll-mt-40">
        {children}
      </h2>
    ),
  };

  const shareUrl = `${site?.siteMetadata?.siteUrl}/impact/meet-our-clients/${prismicCaseStudy.uid}/`;
  const shareTitle = `Abler Nordic Case Study: ${prismicCaseStudy.data.title.text}`;
  const shareSummary = prismicCaseStudy.data.teaser?.text || '';

  // If there is no related content, prismicCaseStudy.data.related should be empty
  // but instead it has one item: { related_content: null }. Let's filter it out.
  prismicCaseStudy.data.related = prismicCaseStudy.data.related.filter((item) => item.related_content);

  return (
    <div className="bg-off-white pb-16 2xl:pb-32">
      <Section className="bg-off-white py-4">
        <div className="max-w-8xl">
          <FadeInOpacity className="flex flex-wrap justify-between pb-12">
            <div className="mx-auto lg:grow lg:mx-0">
              {heroImageOrientation === 'landscape' && landscapeImage ? (
                <figure className="max-w-[800px] lg:max-w-none">
                  <GatsbyImage image={landscapeImage.gatsbyImageData} alt={landscapeImage.alt || ''} className="w-full" />
                </figure>
              ) : mainImage ? (
                <figure className="max-w-[800px] lg:max-w-[572px] mx-auto">
                  <div className="mx-auto lg:aspect-w-3 lg:aspect-h-4">
                    <img src={mainImage.url} alt={mainImage.alt ? mainImage.alt : ''} className="object-cover max-h-[600px] lg:max-h-[none]" />
                  </div>
                </figure>
              ) : (
                <div className="max-w-[800px] lg:max-w-[572px] mx-auto">
                  <div className="bg-nordic-blue-500 px-12 py-10 2xl:px-24 2xl:py-20">
                    <div className="mx-auto lg:aspect-w-3 lg:aspect-h-4">
                      <LogoMonogramWhite className="max-w-[8rem] mx-auto flex items-center" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="lg:w-[60%] xl:w-[53%] flex items-center">
              <Stack className="pt-4 lg:pl-4 2xl:pl-0 mb-16">
                <Link
                  className="inline-block pr-2 font-trade text-[1rem] sm:text-xs 2xl:text-[1.3rem] leading-[1.4] 2xl:leading-[1.2]"
                  to="/technical-assistance/case-studies/"
                >
                  Technical Assistance Case Studies
                </Link>
                <h1 className="text-xl 2xl:text-[5.5rem] 2xl:leading-[1.05] 2xl:mb-10">{prismicCaseStudy.data.title.text}</h1>
                {prismicCaseStudy.data.teaser && (
                  <div className="text-[1.75rem] sm:text-lg 2xl:text-[2.5rem] leading-[1.215] sm:leading-[var(--base-line-height)] 2xl:leading-[1.15] mt-8" dangerouslySetInnerHTML={{ __html: prismicCaseStudy.data.teaser.html }} />
                )}
                {/* Mobile/tablet share — desktop shares live in the nav rail. */}
                <div className="xl:hidden flex items-center gap-2 mt-8">
                  <ShareLinks shareUrl={shareUrl} shareTitle={shareTitle} shareSummary={shareSummary} size="h-10 w-10" />
                </div>
              </Stack>
            </div>
          </FadeInOpacity>
          <FadeInOpacity>
            <dl className="flex flex-wrap divide-y sm:divide-y-0 sm:divide-x divide-soft-black/20 border-t border-soft-black/20 py-6">
              {factStrip.map((fact) => (
                <div key={fact.label} className="w-full sm:w-auto sm:flex-1 sm:px-6 first:sm:pl-0 py-2 sm:py-0">
                  <dt className="font-trade uppercase tracking-[0.12em] text-[0.8rem] 2xl:text-[1rem] opacity-70">{fact.label}</dt>
                  <dd className="font-headline text-[1.25rem] 2xl:text-[1.75rem] leading-tight mt-1">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </FadeInOpacity>
        </div>
      </Section>
      <CaseStudyNav
        variant="horizontal"
        headings={headings}
        shareUrl={shareUrl}
        shareTitle={shareTitle}
        shareSummary={shareSummary}
      />
      <Section className="bg-off-white pt-4 mb-16">
        <div className="max-w-8xl xl:flex xl:gap-12">
          <div className="hidden xl:block xl:grow">
            <CaseStudyNav
              variant="vertical"
              headings={headings}
              shareUrl={shareUrl}
              shareTitle={shareTitle}
              shareSummary={shareSummary}
            />
          </div>
          <div className="xl:w-[53%] xl:shrink-0">
            {/* begin Slices */}
            {prismicCaseStudy.data?.body1 && prismicCaseStudy.data.body1.map((slice, i) => {

              // Safeguard against slice types that we aren't querying for.
              if (!slice.id) return null;

              // Convert id to class.
              const id = `slice-${slice.id.toString().substring(0,5)}-${i}`;
              return (
                <FadeInOpacity
                  key={slice.id.toString()}
                  id={id}
                  className={`slice-${slice.slice_type}`}
                >
                  {(() => {
                    switch (slice.slice_type) {
                      case 'rich_text':
                        return (
                          <div className="mb-16">
                            <RichTextField
                              data={slice.primary.text}
                              components={headingComponents}
                              className="prose prose-custom"
                            />
                          </div>
                        );
                      case 'blockquote':
                        return (
                          <div className="max-w-8xl mx-auto mb-16">
                            <Blockquote slice={slice} colour="nordic-blue-500" />
                          </div>
                        );
                      case 'image_grid':
                        return (
                          <div className="mx-auto max-w-8xl mb-16 flex">
                            <div className={`image-grid image-grid-${slice.items.length} inline-grid items-start gap-4 my-6 2xl:my-16 mx-auto`}>
                              {slice.items.map((item, j) => {
                                return (
                                  <React.Fragment key={`${item.id}-grid-${j}`}>
                                    {item.caption?.html ? (
                                      <FadeInOpacity className="max-w-[900px] mx-auto">
                                        <figure>
                                          <GatsbyImage
                                            image={item.image.thumbnails.Square.gatsbyImageData}
                                            alt={item.image?.alt || ''}
                                            objectFit="contain"
                                          />
                                          {caption
                                            && (
                                            <figcaption
                                              className="italic xl:mx-4"
                                              dangerouslySetInnerHTML={{ __html: item.caption.html }}
                                            />
                                          )}
                                        </figure>
                                      </FadeInOpacity>
                                    ): (
                                      <FadeInOpacity className="max-w-[900px] mx-auto">
                                        <GatsbyImage
                                          image={item.image.thumbnails.Square.gatsbyImageData}
                                          alt={item.image?.thumbnails?.Square?.alt || ''}
                                          objectFit="contain"
                                        />
                                      </FadeInOpacity>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </div>
                          </div>
                        );
                      case 'image___text':
                        return (
                          <div className="mx-auto max-w-8xl flex mb-16">
                            <Switcher className="gap-12">
                              <div className={`${ slice.primary.alignment === 'Align image left' ? ' md:order-2 sm:order-none' : '' }`}>
                                <div
                                  className="prose prose-custom"
                                  dangerouslySetInnerHTML={{ __html: slice.primary.text.html }}
                                />
                              </div>
                              <div className={`flex${ slice.primary.alignment === 'Align image left' ? ' md:order-1 sm:order-none' : '' }`}>
                                <figure className="max-w-[800px] mx-auto">
                                  <GatsbyImage
                                    image={slice.primary.image.gatsbyImageData}
                                    alt=""
                                    objectFit="contain"
                                  />
                                </figure>
                              </div>
                            </Switcher>
                          </div>
                        );

                      case 'info_boxes':
                        return (
                          <div className="mx-auto max-w-8xl flex justify-center mb-16">
                            <InfoBoxes slice={slice} colour="nordic-blue-500" />
                          </div>
                        );

                      default:
                        return '';
                    }
                  })()}
                </FadeInOpacity>
              )
            })}
            {/* end Slices */}
            {/* Related content */}
            {prismicCaseStudy.data.related.length > 0 && (
              <FadeInOpacity className="pb-12">
                <ViewRelatedNews data={prismicCaseStudy.data.related} />
              </FadeInOpacity>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
};

CaseStudy.propTypes = {
  data: PropTypes.shape({
    prismicCaseStudy: PropTypes.shape({
      uid: PropTypes.string,
      data: PropTypes.shape({
        body1: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            slice_type: PropTypes.string,
            primary: PropTypes.shape({
              alignment: PropTypes.string,
              image: PropTypes.shape({
                gatsbyImageData: PropTypes.object,
                alt: PropTypes.string,
              }),
              text: PropTypes.shape({
                html: PropTypes.string,
              }),
            }),
            items: PropTypes.arrayOf(
              PropTypes.shape({
                image: PropTypes.shape({
                  gatsbyImageData: PropTypes.object,
                  alt: PropTypes.string,
                }),
              }),
            ),
          }),
        ),
      }),
    }),
  }),
};

export default withPrismicPreview(CaseStudy);

export const pageQuery = graphql`
  query ($uid: String!) {
    site {
      siteMetadata {
        title
        description
        author
        siteUrl
      }
    }
    prismicCaseStudy(
      uid: {
        eq: $uid
      }
    ) {
      id
      uid
      _previewable
      ...PrismicCaseStudyFragment
    }
  }
`;
